import { Text } from "@imminently/react/ui/core/text";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Button, Stack } from "@mui/material";
import { PropsWithChildren } from "react";

import { TooltipIcon, WarningIcon } from "@/assets/icons";
import { ExternalRegisterDot } from "@/components/externalregister/ExternalRegisterDot";
import { getPatientExternalRegisterAlerts, type PatientEncounterWithExternalRegisterSummary } from "@/components/externalregister/patientSummary";
import { NEWS2Score } from "@/resources/types";
import { RISK_COLOURS, RISK_LABELS } from "@/resources/constants";

const styles = {
  notification: {
    color: "white",
    padding: "0.75rem 2rem",
    alignItems: "center",
    gap: 1,
  },
};

type NotificationProps = PropsWithChildren & {
  color?: string;
  backgroundColor?: string;
};

const Notification = (props: NotificationProps) => {
  const { color = "white", backgroundColor = "primary.300", children } = props;

  return (
    <Stack
      direction="row"
      sx={{ ...styles.notification, color, backgroundColor }}
    >
      {children}
    </Stack>
  );
};

const FallsRiskNotification = () => (
  <Notification backgroundColor={"#f8d60a"}>
    <WarningIcon css={{ color: "#000", verticalAlign: "text-bottom" }} />
    <Text bold color="black">
      Falls risk
    </Text>
  </Notification>
);

const InfectiousNotification = () => (
  <Notification backgroundColor="error.dark">
    <WarningIcon css={{ verticalAlign: "text-bottom" }} />
    <Text bold color="white">
      Infection risk
    </Text>
  </Notification>
);

const SepsisNotifcation = ({ riskScore }: { riskScore: NEWS2Score }) => {
  const { risk, score } = riskScore;

  let riskLabel = RISK_LABELS[risk].title;

  if (riskLabel) {
    riskLabel += " -";
  }

  return (
    <Notification color="primary" backgroundColor={RISK_COLOURS[risk].main}>
      <Text sx={{ mr: "auto" }} bold>
        Risk Score
      </Text>
      <Text bold>
        {riskLabel} {score}
      </Text>
    </Notification>
  );
};

const ExternalRegisterNotification = ({
  encounter,
  onOpenDetails,
}: {
  encounter: PatientEncounterWithExternalRegisterSummary;
  onOpenDetails?: (alertId?: string) => void;
}) => {
  const alerts = getPatientExternalRegisterAlerts(encounter.patient);
  if (alerts.length === 0) return null;

  return (
    <Notification color="text.primary" backgroundColor="grey.100">
      <InfoOutlinedIcon color="primary" fontSize="small" />
      <Stack direction="row" gap={0.75} alignItems="center" sx={{ mr: "auto" }}>
        <ExternalRegisterDot alerts={alerts} label="Additional notification present" />
      </Stack>
      <Button
        size="small"
        variant="outlined"
        color="primary"
        disabled={!onOpenDetails}
        onClick={() => onOpenDetails?.(alerts.length === 1 ? alerts[0].id : undefined)}
      >
        View details
      </Button>
    </Notification>
  );
};

type NotificationsProps = {
  encounter: PatientEncounterWithExternalRegisterSummary;
  showExternalRegisterAlerts?: boolean;
  onOpenExternalRegisterDetails?: (alertId?: string) => void;
};

const PatientNotifications = ({
  encounter,
  showExternalRegisterAlerts = true,
  onOpenExternalRegisterDetails,
}: NotificationsProps) => {
  const { riskScore, infectiousSymptoms, frailty, fallRisk } = encounter;
  const notifications: React.ReactNode[] = [];

  // if (riskScore && riskScore.risk !== ClinicalRisk.LOW) {
  if (riskScore) {
    notifications.push(<SepsisNotifcation key="risk-score" riskScore={riskScore} />);
  }

  if (infectiousSymptoms && infectiousSymptoms.length > 0) {
    notifications.push(<InfectiousNotification key="infectious" />);
  }

  if (
    (fallRisk && Object.values(fallRisk).some(Boolean)) ||
    (frailty && frailty >= 5)
  ) {
    notifications.push(<FallsRiskNotification key="falls" />);
  }

  const externalRegisterAlerts = getPatientExternalRegisterAlerts(encounter.patient);
  if (showExternalRegisterAlerts && externalRegisterAlerts.length > 0) {
    notifications.push(
      <ExternalRegisterNotification
        key="external-register"
        encounter={encounter}
        onOpenDetails={onOpenExternalRegisterDetails}
      />,
    );
  }

  return (
    <Stack borderRadius="1rem 1rem 0 0" overflow="hidden">
      {notifications.length > 0 ? (
        notifications
      ) : (
        <Notification>
          <TooltipIcon css={{ verticalAlign: "text-bottom" }} />
          <Text bold>No notifications</Text>
        </Notification>
      )}
    </Stack>
  );
};

export default PatientNotifications;
